import { useState, useEffect } from 'react';
import { Camera, FileText } from 'lucide-react';
import { api } from '../../lib/api-client';

export function FotoThumbnail({ s3Key, filename, mimeType }: { s3Key: string; filename: string; mimeType?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [imgError, setImgError] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setImgError(false);
    setUrl(null);

    api
      .get<{ data: { download_url: string; expires_in: number } }>('/uploads/download', { params: { key: s3Key } })
      .then((res) => {
        if (!cancelled) setUrl(res.data.data.download_url);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [s3Key]);

  if (loading && !url) {
    return (
      <div className="aspect-square bg-gray-100 rounded-lg flex flex-col items-center justify-center gap-1 animate-pulse">
        <Camera className="w-5 h-5 text-gray-300" />
        <span className="text-[10px] text-gray-300">Cargando...</span>
      </div>
    );
  }

  const isPdf = mimeType === 'application/pdf' || filename.toLowerCase().endsWith('.pdf');

  if (!url) {
    return (
      <div className="aspect-square bg-gray-100 rounded-lg flex flex-col items-center justify-center text-xs text-gray-400 overflow-hidden p-2">
        {isPdf ? <FileText className="w-5 h-5 text-red-300 mb-1" /> : <Camera className="w-5 h-5 text-gray-300 mb-1" />}
        <span className="truncate w-full text-center">{filename}</span>
      </div>
    );
  }

  // PDF: show as a clickable card that opens in new tab
  if (isPdf) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full aspect-square rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 transition-colors flex flex-col items-center justify-center gap-1 p-2 group"
      >
        <FileText className="w-8 h-8 text-red-400 group-hover:text-red-500" />
        <span className="text-[10px] text-red-500 truncate w-full text-center leading-tight">{filename}</span>
      </a>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setShowFullImage(true)}
        className="block w-full relative group rounded-lg overflow-hidden bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-blue"
      >
        {!imgError ? (
          <img
            src={url}
            alt={filename}
            crossOrigin="anonymous"
            className="aspect-square w-full object-cover"
            loading="lazy"
            onLoad={() => setLoading(false)}
            onError={() => { setImgError(true); setLoading(false); }}
          />
        ) : (
          <div className="aspect-square w-full flex flex-col items-center justify-center text-gray-400 p-2">
            <Camera className="w-5 h-5 text-gray-300 mb-1" />
            <span className="text-[10px] truncate w-full text-center">{filename}</span>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <p className="truncate text-[10px] text-white">{filename}</p>
        </div>
      </button>

      {showFullImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setShowFullImage(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={url}
              alt={filename}
              crossOrigin="anonymous"
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <div className="mt-2 flex items-center justify-between">
              <p className="text-sm text-white/80 truncate">{filename}</p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="ml-3 text-xs text-blue-300 hover:text-blue-200 underline whitespace-nowrap"
              >
                Abrir en nueva pestaña
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
